// frontend/src/pages/marketplace/CategoryPage.tsx
// Category page showing products within a specific category

import React, { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Grid, List, Loader2, SlidersHorizontal, X } from 'lucide-react';
import {
  CategoryBreadcrumb,
  CategoryNav,
  MarketplaceFilters,
  ProductCard,
} from '../components';
import {
  useCategory,
  useCategories,
  useInfiniteProducts,
} from '../hooks/useMarketplace';
import { useMarketplaceStore } from '../stores/marketplaceStore';
import type { ProductFilters } from '../types/marketplaceTypes';

const CategoryPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  const { filters, setFilters, resetFilters } = useMarketplaceStore();
  const { data: category, isLoading: categoryLoading } = useCategory(slug || '');
  const { data: categories } = useCategories();

  // Build filters from URL params and store
  const productFilters: ProductFilters = {
    categorySlug: slug,
    priceMin: searchParams.get('priceMin') ? Number(searchParams.get('priceMin')) : undefined,
    priceMax: searchParams.get('priceMax') ? Number(searchParams.get('priceMax')) : undefined,
    minRating: searchParams.get('minRating') ? Number(searchParams.get('minRating')) : undefined,
    acceptsInsurance: searchParams.get('insurance') === 'true' ? true : undefined,
    sortBy: (searchParams.get('sortBy') as ProductFilters['sortBy']) || 'rating',
    sortOrder: (searchParams.get('sortOrder') as ProductFilters['sortOrder']) || 'desc',
    ...filters,
  };

  const {
    data: productsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: productsLoading,
  } = useInfiniteProducts(productFilters);

  // Flatten paginated products
  const products = productsData?.pages.flatMap((page) => page.items) || [];
  const totalCount = productsData?.pages[0]?.total || 0;

  const handleFilterChange = (newFilters: Partial<ProductFilters>) => {
    const params = new URLSearchParams(searchParams);
    
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      } else {
        params.delete(key);
      }
    });
    
    setSearchParams(params);
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setSearchParams({});
    resetFilters();
  };

  const activeFilterCount = Object.values(productFilters).filter(
    (v) => v !== undefined && v !== null && v !== '' && v !== slug
  ).length;

  if (categoryLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sage animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumb */}
          {categories && (
            <CategoryBreadcrumb
              categorySlug={slug || ''}
              categories={categories}
              className="mb-4"
            />
          )}

          {/* Category Info */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-semibold text-navy">
                {category?.name || 'Category'}
              </h1>
              {category?.description && (
                <p className="text-warm-gray mt-1 max-w-2xl">
                  {category.description}
                </p>
              )}
              <p className="text-sm text-warm-gray mt-2">
                {totalCount} {totalCount === 1 ? 'product' : 'products'} found
              </p>
            </div>

            {/* View Controls */}
            <div className="flex items-center gap-4">
              {/* Sort Dropdown */}
              <select
                value={`${productFilters.sortBy}-${productFilters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('-');
                  handleFilterChange({
                    sortBy: sortBy as ProductFilters['sortBy'],
                    sortOrder: sortOrder as ProductFilters['sortOrder'],
                  });
                }}
                className="px-3 py-2 bg-cream border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-sage/20 focus:border-sage"
              >
                <option value="rating-desc">Highest Rated</option>
                <option value="rating-asc">Lowest Rated</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="created_at-desc">Newest First</option>
                <option value="name-asc">Name: A-Z</option>
              </select>

              {/* View Toggle */}
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-sage text-white'
                      : 'bg-white text-warm-gray hover:bg-cream'
                  }`}
                  title="Grid view"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 transition-colors ${
                    viewMode === 'list'
                      ? 'bg-sage text-white'
                      : 'bg-white text-warm-gray hover:bg-cream'
                  }`}
                  title="List view"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              {/* Mobile Filter Toggle */}
              <button
                onClick={() => setShowMobileFilters(true)}
                className="lg:hidden flex items-center gap-2 px-3 py-2 bg-sage text-white rounded-lg"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-white/20 rounded-full text-xs">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-navy">Filters</h2>
                {activeFilterCount > 0 && (
                  <button
                    onClick={handleResetFilters}
                    className="text-sm text-sage hover:text-sage-dark"
                  >
                    Clear all
                  </button>
                )}
              </div>

              <MarketplaceFilters
                filters={productFilters}
                onChange={handleFilterChange}
                onReset={handleResetFilters}
                orientation="vertical"
              />

              {/* Subcategories */}
              {categories && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-medium text-navy mb-3">Categories</h3>
                  <CategoryNav
                    variant="sidebar"
                    activeCategory={slug}
                    showCounts
                  />
                </div>
              )}
            </div>
          </aside>

          {/* Product Grid */}
          <main className="flex-1">
            {productsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-sage animate-spin" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-cream-dark rounded-full flex items-center justify-center mx-auto mb-4">
                  <Grid className="w-8 h-8 text-warm-gray" />
                </div>
                <h3 className="font-display text-lg font-semibold text-navy mb-2">
                  No products found
                </h3>
                <p className="text-warm-gray mb-4">
                  Try adjusting your filters or search criteria.
                </p>
                <button
                  onClick={handleResetFilters}
                  className="text-sage hover:text-sage-dark font-medium"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              <>
                <div
                  className={
                    viewMode === 'grid'
                      ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6'
                      : 'space-y-4'
                  }
                >
                  {products.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      variant={viewMode === 'list' ? 'horizontal' : 'default'}
                      showWishlist
                    />
                  ))}
                </div>

                {/* Load More */}
                {hasNextPage && (
                  <div className="mt-8 text-center">
                    <button
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className="px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50"
                    >
                      {isFetchingNextPage ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading...
                        </span>
                      ) : (
                        'Load More Products'
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMobileFilters(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="font-semibold text-navy">Filters</h2>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 text-warm-gray hover:text-navy"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto h-full pb-32">
              <MarketplaceFilters
                filters={productFilters}
                onChange={handleFilterChange}
                onReset={handleResetFilters}
                orientation="vertical"
              />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100">
              <div className="flex gap-3">
                <button
                  onClick={handleResetFilters}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-navy rounded-lg hover:bg-cream transition-colors"
                >
                  Clear All
                </button>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="flex-1 px-4 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
                >
                  Show Results
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryPage;


